import * as React from 'react';
import { ClassNames } from '@emotion/core';

import type { ClassNames as DayPickerClassNames } from 'react-day-picker/types/ClassNames';

import { Colors } from '../../../theme/Colors';
import { Typography } from '../../../theme/Typography';

type ChildrenProps = {
  classNames: DayPickerClassNames;
  modifiersStyles: {};
};

type Props = {
  children: (styleProps: ChildrenProps) => React.ReactNode;
  size: 'medium' | 'small';
};

export function WithDayPickerStyle({ children, size }: Props) {
  return (
    <ClassNames>
      {({ css }) =>
        children({
          classNames: {
            body: css`
              display: table-row-group;
            `,
            caption: css`
              color: ${Colors.black};
              margin: 0 0 1em 0;
              text-align: center;
              ${Typography.subtitle700}
            `,
            container: css`
              display: flex;
              justify-content: center;
              position: relative;
              user-select: none;

              &:after,
              &:before {
                border: solid transparent;
                bottom: 100%;
                content: ' ';
                display: block;
                height: 0;
                left: 10%;
                pointer-events: none;
                position: absolute;
                width: 0;
              }

              &:after {
                border-bottom-color: ${Colors.black};
                border-color: rgba(136, 183, 213, 0);
                border-width: 8px;
                margin-left: -8px;
              }

              &:before {
                border-bottom-color: ${Colors.black};
                border-color: rgba(194, 225, 245, 0);
                border-width: 8px;
                margin-left: -8px;
              }

              abbr {
                ${Typography.allCaps800}
                text-decoration: none;
              }
            `,
            day: css`
              border: none;
              cursor: pointer;
              display: table-cell;
              padding: 0.8rem;
              text-align: center;
              vertical-align: middle;
              ${Typography.bodyText800}
            `,
            interactionDisabled: css`
              .DayPicker-Day {
                cursor: default;
              }
            `,
            // @ts-ignore This is valid, but the types don't reflect it: https://react-day-picker.js.org/api/DayPicker/
            month: css`
              border-collapse: collapse;
              border-spacing: 0;
              padding: 1.7em 1em 1em 1em;
              user-select: none;
              ${size !== 'small'
                ? `
                &:last-of-type {
                  border-left: 1px solid ${Colors.grey100};
                }
              `
                : ''}
            `,
            // @ts-ignore This is valid, but the types don't reflect it: https://react-day-picker.js.org/api/DayPicker/
            months: css`
              display: flex;
            `,
            navBar: '',
            navButtonNext: '',
            navButtonPrev: '',
            navButtonInteractionDisabled: '',
            footer: '',
            todayButton: '',
            week: css`
              display: table-row;
            `,
            weekNumber: '',
            weekday: css`
              display: table-cell;
              font-size: 0.875em;
              padding: 0.8rem;
              text-align: center;
            `,
            weekdays: css`
              display: table-header-group;

              > div {
                display: table-row;
              }
            `,
            weekdaysRow: '',
            wrapper: css`
              display: flex;
              position: relative;
            `,

            // Modifiers
            outside: css`
              color: ${Colors.grey400};
              cursor: default;
            `,
            selected: css`
              background-color: ${Colors.blue100};
              color: ${Colors.blue500};
              font-weight: 600;
            `,
            today: css`
              color: ${Colors.grey900};
              font-weight: 700;
            `,
            disabled: css`
              background-color: ${Colors.grey000};
              color: ${Colors.grey500};
              cursor: not-allowed;
              outline: none;
            `,
          },
          modifiersStyles: {
            from: {
              borderBottomLeftRadius: 10,
              borderTopLeftRadius: 10,
            },
            to: {
              borderBottomRightRadius: 10,
              borderTopRightRadius: 10,
            },
          },
        })
      }
    </ClassNames>
  );
}
